import { Grid2, Menu, MenuItem, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { FieldArray, FieldArrayRenderProps, FormikProps } from "formik";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { Translation } from "reduxState/translations/translationsSlice";
import { RewardFields } from "types/rewards";
import { TranslationTabs } from "../../../../TranslationTabs/TranslationTabs";
import { useStyles } from "../RewardForm.styles";
import { TranslationFields } from "./TranslationFields";

type Props = {
  availableLanguages: Translation[];
  activeTranslation: number;
  setActiveTranslation: (index: number) => void;
  isEditMode?: boolean;
};

export function AdditionalSettings({
  availableLanguages,
  activeTranslation,
  setActiveTranslation,
  isEditMode,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const contextMenu = useContextMenu();

  return (
    <OLPaper className={classes.OLPaper}>
      <FieldArray
        name="translations"
        render={({ form, push }: FieldArrayRenderProps) => {
          const { values }: FormikProps<RewardFields> = form;
          const { setFieldValue } = form;

          return (
            <>
              <Grid2 container spacing={8}>
                <TranslationFields
                  index={0}
                  translation={values.translations[0]}
                  setFieldValue={setFieldValue}
                  isEditMode={isEditMode}
                />
                {values.translations.length === 1 &&
                availableLanguages.length ? (
                  <>
                    <OLButton
                      className={classes.addTranslation}
                      color="secondary"
                      onClick={
                        availableLanguages.filter(
                          (lang: any) =>
                            !values.translations.find(
                              (openLang) =>
                                (openLang?.code || openLang?.locale) ===
                                lang?.code,
                            ),
                        ).length
                          ? contextMenu.handleMenuOpen
                          : undefined
                      }
                      startIcon={<PlusIcon />}
                      data-test-id="reward-form-additional-settings-add-translations-button"
                    >
                      {message({ id: "addTranslation" })}
                    </OLButton>
                    <Menu
                      anchorEl={contextMenu.anchor}
                      anchorOrigin={{
                        horizontal: "right",
                        vertical: "top",
                      }}
                      onClose={contextMenu.handleMenuClose}
                      open={contextMenu.isMenuOpen}
                      transformOrigin={{
                        horizontal: "right",
                        vertical: "top",
                      }}
                    >
                      {availableLanguages
                        .filter(
                          (lang) =>
                            !values.translations.find(
                              (openLang) =>
                                (openLang?.code || openLang?.locale) ===
                                lang.code,
                            ),
                        )
                        .map((language) => (
                          <MenuItem
                            key={language.code}
                            onClick={() => {
                              contextMenu.handleMenuClose();
                              push({
                                code: language.code,
                                languageName: language.name,
                              });
                            }}
                          >
                            {language.name}
                          </MenuItem>
                        ))}
                    </Menu>
                  </>
                ) : null}
                {values?.translations?.length > 1 ? (
                  <>
                    <Grid2 size={12}>
                      <Typography className={classes.sectionHeader}>
                        {message({ id: "translations" })}
                      </Typography>
                    </Grid2>
                    <TranslationTabs
                      activeTranslation={activeTranslation}
                      setActiveTranslation={setActiveTranslation}
                      translations={values.translations}
                      availableLanguages={availableLanguages}
                      push={push}
                      setFieldValue={setFieldValue}
                      isEditMode={isEditMode}
                    />
                    <TranslationFields
                      index={activeTranslation}
                      translation={values.translations[activeTranslation]}
                      setFieldValue={setFieldValue}
                      isEditMode={isEditMode}
                    />
                  </>
                ) : null}
              </Grid2>
            </>
          );
        }}
      />
    </OLPaper>
  );
}
